import { isOfficalAddress } from '@/utils';
import type { Spread } from 'lexical';

import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';

export type SerializedMentionNode = Spread<
  {
    mentionName: string;
    data?: UnionAccount;
    type: 'mention';
    version: 1;
  },
  SerializedTextNode
>;

function convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;
  const data = domNode.dataset.data ? JSON.parse(domNode.dataset.data) : undefined;
  if (textContent !== null) {
    const node = $createMentionNode(textContent, data);
    return {
      node,
    };
  }

  return null;
}

const mentionUserStyle =
  'margin: 1px 2px; display: inline-block; color: var(--dark-blue-title); border: 1px solid var(--select-divider); border-radius: 43px; padding: 0 4px; line-height: 22px;';

const mentionOfficialStyle = `border: 1px solid rgba(80, 137, 255, 0.3); border-radius: 43px; color: var(--color-blue);`;
export class MentionNode extends TextNode {
  __mention: string;
  __data: UnionAccount | undefined;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__text, node.__key, node.__data);
  }
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.mentionName, serializedNode.data);
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(mentionName: string, text?: string, key?: NodeKey, data?: UnionAccount) {
    super(text ?? mentionName, key);
    this.__mention = mentionName;
    this.__data = data;
  }

  getData(): UnionAccount | undefined {
    return this.__data;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      data: this.getData(),
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    config.disableEvents = true;
    const dom = super.createDOM(config);
    const account = this.getData();
    dom.style.cssText = isOfficalAddress(account?.id)
      ? mentionUserStyle + mentionOfficialStyle
      : mentionUserStyle;
    dom.dataset.data = JSON.stringify(this.getData());
    dom.className = 'mention';
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.textContent = this.__text;
    element.dataset.data = JSON.stringify(this.getData());
    return { element };
  }

  // avoid edit
  split(): any {
    return;
  }
  splitText(): any {
    return;
  }

  isSegmented(): true {
    return true;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  isToken(): true {
    return true;
  }
}

export function $createMentionNode(mentionName: string, data?: UnionAccount): MentionNode {
  const mentionNode = new MentionNode(mentionName, undefined, undefined, data);
  mentionNode.setMode('segmented').toggleDirectionless();
  return mentionNode;
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
